export enum EnvironmentType {
  Development,
  Staging,
  Production,
}

export abstract class EnvironmentBase implements Environment {
  /** Name of the environment used when running the client locally, see LocalEnvironment. */
  public static readonly localEnvironmentName = 'local';

  public abstract readonly name: string;
  public abstract readonly selfBaseUrl: string;
  public abstract readonly cookieNames: LocalStorageKeys;
  public abstract readonly showDebug: boolean;
  public abstract readonly apiBaseUrls?: ApiBaseUrls;
  public abstract readonly chargebeeSiteName: string;
  public abstract readonly gtmId: string;
  public abstract readonly appInsightsInstrumentationKey: string;
  public readonly zapierEmbedClientId: string = 'zB330syuN9UR2IIFpodLfonwiose5CsEzGAuHuVZ';
  public abstract previewSiteUrl: string;
  public abstract blankTemplateId: number;
  public abstract readonly trackerDomain: string;
  public abstract readonly turnStileApiKey: string;
  public abstract readonly shopifyAppInstallUrl: string;
  public abstract readonly defaultFromEmail: string;

  public readonly useMocks: boolean = false;

  public readonly isProduction: boolean;
  public readonly isStaging: boolean;
  public readonly isDevelopment: boolean;
  public readonly rolesMayDiffer: boolean;

  constructor(public readonly type: EnvironmentType) {
    if (!(this.type in EnvironmentType)) {
      throw new Error(`Unknown environment type : ${this.type}`);
    }
    // Cache the values
    this.isProduction = this.type === EnvironmentType.Production;
    this.isDevelopment = [EnvironmentType.Development].indexOf(this.type) !== -1;
    this.isStaging = [EnvironmentType.Staging].indexOf(this.type) !== -1;
    // Identity refuses different roles on a person's sites until Postgres is its only user store, which only dev is.
    this.rolesMayDiffer = this.isDevelopment;
  }
}

export interface Environment {
  readonly name: string;
  readonly apiBaseUrls?: ApiBaseUrls;
  readonly selfBaseUrl: string;
  readonly cookieNames: LocalStorageKeys;
  readonly showDebug: boolean;
  readonly useMocks: boolean;
  readonly isProduction: boolean;
  readonly isDevelopment: boolean;
  readonly isStaging: boolean;
  /** Whether the users page sets account admin per site instead of on all of a person's sites. */
  readonly rolesMayDiffer: boolean;
  readonly chargebeeSiteName: string;
  readonly gtmId: string;
  readonly zapierEmbedClientId: string;
  readonly appInsightsInstrumentationKey: string;
  readonly previewSiteUrl: string;
  readonly trackerDomain: string;
  readonly blankTemplateId: number;
  readonly turnStileApiKey: string;
  readonly shopifyAppInstallUrl: string;
  readonly defaultFromEmail: string;
}

export interface ApiBaseUrls {
  gateway: string;
}

export interface LocalStorageKeys {
  peopleColumns: string;
  currentContext: string;
}
